import { format, isToday, parseISO } from 'date-fns';
import {
  type PopUpNotificationProperties,
  UserNotificationType,
} from 'dynamo-daos/build/models/misc/userNotificationProperties';
import type { MouseEvent, ReactNode, Ref } from 'react';

import type { UserNotification } from '@cartwhl/common/types';
import { resolveNotificationLinkUrl } from '@cartwhl/common/utils/resolveNotificationLinkUrl';
import Text from '@cartwhl/ui/base/TextNew';

export type NotificationListItemProps = {
  notification: UserNotification;
  onMarkRead: (notificationID: string) => void | Promise<void>;
};

function formatNotificationTime(createdAt: string) {
  try {
    const date = parseISO(createdAt);
    const time = format(date, 'hh:mm a').toLowerCase();
    if (isToday(date)) {
      return time;
    }
    return `${format(date, 'MM/dd')}`;
  } catch {
    return '';
  }
}

export function NotificationListItemLayout({
  children,
  href,
  notification,
  onMarkRead,
  icon,
  itemRef,
}: {
  children: ReactNode;
  href?: string;
  notification: Pick<
    UserNotification,
    'notificationID' | 'status' | 'createdAt'
  >;
  onMarkRead?: (notificationID: string) => void | Promise<void>;
  icon?: ReactNode;
  itemRef?: Ref<HTMLLIElement>;
}) {
  const unread =
    notification.status === 'new' || notification.status === 'unread';

  const markReadIfNeeded = async () => {
    if (notification.status !== 'read') {
      await onMarkRead?.(notification.notificationID);
    }
  };

  const surfaceClassName =
    'hover:bg-gray-15 dark:hover:bg-gray-90 outline-hidden mr-1 block rounded-lg transition-colors';
  const rowClassName = `flex items-start gap-3 px-4 ${icon ? 'py-3' : 'py-2'}`;

  const row = (
    <div className={rowClassName}>
      <div className="mt-0.5 h-5 w-5 shrink-0">{icon}</div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
      <div className="flex shrink-0 items-center gap-2 self-center">
        <Text token="12" className="text-tertiary whitespace-nowrap">
          {formatNotificationTime(notification.createdAt)}
        </Text>
        {unread ? (
          <span
            className="bg-green h-2 w-2 shrink-0 rounded-full"
            aria-label="Unseen update"
          />
        ) : (
          <span className="h-2 w-2 shrink-0" aria-hidden />
        )}
      </div>
    </div>
  );

  return (
    <li
      ref={itemRef}
      className="border-primary border-b border-solid py-1 last:border-b-0"
    >
      {href ? (
        <NotificationListItemLink
          href={href}
          className={surfaceClassName}
          onActivate={markReadIfNeeded}
        >
          {row}
        </NotificationListItemLink>
      ) : (
        <div>{row}</div>
      )}
    </li>
  );
}

function NotificationListItemLink({
  children,
  className,
  href,
  onActivate,
}: {
  children: ReactNode;
  className: string;
  href: string;
  onActivate: () => void | Promise<void>;
}) {
  const url = new URL(href, window.location.origin);

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    try {
      await onActivate();
    } finally {
      window.location.assign(url.href);
    }
  };

  return (
    <a href={url.href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

function PopUpNotificationListItem({
  notification,
  onMarkRead,
}: NotificationListItemProps) {
  const { link, subtitle, title } =
    notification.properties as unknown as PopUpNotificationProperties;

  const href = link ? resolveNotificationLinkUrl(link) : undefined;

  return (
    <NotificationListItemLayout
      notification={notification}
      href={href}
      onMarkRead={onMarkRead}
    >
      <Text token="14/medium" className="text-primary truncate">
        {title ?? ''}
      </Text>
      <Text token="14" className="text-tertiary text-pretty break-words">
        {subtitle ?? ''}
      </Text>
    </NotificationListItemLayout>
  );
}

export default function NotificationListItem(props: NotificationListItemProps) {
  switch (props.notification.notificationType) {
    case UserNotificationType.POP_UP:
      return <PopUpNotificationListItem {...props} />;
    default:
      return null;
  }
}
